import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Mail, Phone } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", notes: "" };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/suppliers");
    setSuppliers(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({
    name: s.name, email: s.email || "", phone: s.phone || "", notes: s.notes || "",
  }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, email: form.email || null, phone: form.phone || null, notes: form.notes || null };
    try {
      if (editing) {
        await api.put(`/suppliers/${editing}`, payload);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", payload);
        toast.success("Supplier added");
      }
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const del = async (id) => {
    if (!confirm("Delete this supplier?")) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
            Vendors
          </div>
          <h1 className="font-display text-4xl font-bold">Suppliers</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage vendors and their contact info for automated ordering.
          </p>
        </div>
        <button onClick={openNew} className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2" data-testid="add-supplier-btn">
          <Plus className="w-4 h-4" /> Add supplier
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="surface-card rounded-xl p-12 text-center text-slate-500">
          <div className="font-display text-2xl mb-2">No suppliers yet</div>
          <p className="text-sm">Add your first supplier to enable automated POs.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((s) => (
            <div key={s.id} className="surface-card rounded-xl p-5 group" data-testid={`supplier-card-${s.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="font-display text-xl font-bold">{s.name}</div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(s)} className="p-1.5 hover:bg-white/[0.05] rounded" data-testid={`edit-supplier-${s.id}`}>
                    <Pencil className="w-4 h-4 text-slate-400" />
                  </button>
                  <button onClick={() => del(s.id)} className="p-1.5 hover:bg-red-500/10 rounded" data-testid={`delete-supplier-${s.id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                {s.email && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="font-mono-plex text-xs">{s.email}</span>
                  </div>
                )}
                {s.phone && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Phone className="w-3.5 h-3.5" />
                    <span className="font-mono-plex text-xs">{s.phone}</span>
                  </div>
                )}
              </div>
              {s.notes && <div className="text-xs text-slate-500 mt-3 italic">{s.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <form onSubmit={save} className="w-full max-w-md bg-[#12141a] border border-white/10 rounded-xl p-6 space-y-4" data-testid="supplier-form">
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl font-bold">{editing ? "Edit supplier" : "Add supplier"}</div>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <Field label="Name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="sup-name" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="sup-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} data-testid="sup-phone" />
            </Field>
            <Field label="Notes">
              <textarea rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inp} data-testid="sup-notes" />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
              <button type="submit" className="btn-amber rounded-md px-4 py-2 text-sm" data-testid="save-supplier">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inp = "w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none focus:border-amber-500";
const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-xs text-slate-400 mb-1">{label} {required && <span className="text-amber-500">*</span>}</div>
    {children}
  </label>
);
